import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  User, 
  LogOut, 
  Calendar, 
  Clock, 
  FileText, 
  Phone,
  ChevronRight,
  RefreshCw,
  PlusCircle
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientDashboardPage = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientEmail, setClientEmail] = useState("");

  useEffect(() => {
    const isAuth = localStorage.getItem("clientAuth");
    const email = localStorage.getItem("clientEmail");
    const phone = localStorage.getItem("clientPhone");
    
    if (!isAuth || !email || !phone) {
      navigate("/my-quotes/login");
      return;
    }
    
    setClientEmail(email);
    fetchQuotes(email, phone);
  }, [navigate]);

  const fetchQuotes = async (email, phone) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/client/quotes`, {
        params: { email, phone }
      });
      setQuotes(response.data.quotes || []);
    } catch (error) {
      console.error("Error fetching quotes:", error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("clientAuth");
    localStorage.removeItem("clientEmail");
    localStorage.removeItem("clientPhone");
    navigate("/my-quotes/login");
  };

  const handleRefresh = () => {
    const email = localStorage.getItem("clientEmail");
    const phone = localStorage.getItem("clientPhone");
    if (email && phone) {
      fetchQuotes(email, phone);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { label: "Submitted", className: "bg-blue-500 text-white" },
      contacted: { label: "In Contact", className: "bg-yellow-500 text-white" },
      quoted: { label: "Quote Sent", className: "bg-purple-500 text-white" },
      booked: { label: "Job Booked", className: "bg-green-500 text-white" },
      completed: { label: "Completed", className: "bg-emerald-600 text-white" },
      archived: { label: "Closed", className: "bg-gray-500 text-white" }
    };
    
    const config = statusConfig[status] || { label: status, className: "bg-gray-500 text-white" };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getServiceLabel = (service) => {
    const labels = {
      doors: "Door Fitting",
      trimwork: "Skirting & Architraves",
      panelling: "Wall Panelling",
      multiple: "Multiple Services",
      carpentry: "Carpentry",
      both: "Multiple Services"
    };
    return labels[service] || service;
  };

  return (
    <div className="min-h-[70vh] bg-[#f8f7f5] py-6 sm:py-10">
      <Helmet>
        <title>My Quotes | Expert Carpenter</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#3A3A3A]">My Quotes</h1>
            <p className="text-gray-500 text-sm sm:text-base mt-1">
              Logged in as {clientEmail}
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="flex-1 sm:flex-none h-10"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex-1 sm:flex-none h-10"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Log Out
            </Button>
          </div>
        </div>

        {/* Quotes List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-[#2D5A4A]" />
            <p className="mt-4 text-gray-500">Loading your quotes...</p>
          </div>
        ) : quotes.length === 0 ? (
          <Card className="p-8 sm:p-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-[#3A3A3A] mb-2">No Quotes Found</h2>
            <p className="text-gray-500 mb-6">
              You haven't submitted any quote requests yet.
            </p>
            <Link to="/contact">
              <Button className="bg-[#2D5A4A] hover:bg-[#234839]">
                <PlusCircle className="h-5 w-5 mr-2" />
                Request a Quote
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-4">
            {quotes.map((quote, index) => (
              <Card key={quote.id || index} className="p-4 sm:p-6 bg-white">
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {getStatusBadge(quote.status)}
                      <Badge variant="outline" className="text-[#2D5A4A] border-[#2D5A4A]">
                        {getServiceLabel(quote.service_type)}
                      </Badge>
                    </div>
                    
                    <p className="text-[#3A3A3A] font-medium mb-3 text-sm sm:text-base">
                      {quote.message}
                    </p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="h-4 w-4" />
                        {formatDate(quote.timestamp)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-4 w-4" />
                        {formatTime(quote.timestamp)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Status indicator on larger screens */}
                  <div className="hidden sm:flex items-center">
                    <div className={`w-3 h-3 rounded-full ${
                      quote.status === "completed" ? "bg-emerald-500" :
                      quote.status === "booked" ? "bg-green-500" :
                      quote.status === "quoted" ? "bg-purple-500" :
                      quote.status === "contacted" ? "bg-yellow-500" :
                      "bg-blue-500"
                    }`} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Help Section */}
        <Card className="mt-8 p-4 sm:p-6 bg-[#2D5A4A] text-white">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-lg mb-1">Need to discuss your quote?</h3>
              <p className="text-white/80 text-sm">Give me a call and I'll be happy to help.</p>
            </div>
            <a href="tel:07300405045">
              <Button className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-semibold w-full sm:w-auto">
                <Phone className="h-5 w-5 mr-2" />
                07300 405045
              </Button>
            </a>
          </div>
        </Card>

        {/* Request Another Quote */}
        <div className="mt-6 text-center">
          <Link to="/contact">
            <Button variant="outline" className="border-[#2D5A4A] text-[#2D5A4A]">
              Request Another Quote
              <ChevronRight className="h-5 w-5 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboardPage;
